import { LoaderFunctionArgs } from '@remix-run/node'
import {
	Link,
	defer,
	isRouteErrorResponse,
	useLoaderData,
	useRouteError,
} from '@remix-run/react'
import {
	getAttestationv2,
	getDerivedAttestationsForRollupCID,
	getEventDataByRollupCID,
} from '#app/server/db.server.ts'
import MetadataProfile from '#app/components/metadataProfile/rollupMetadataProfile.tsx'
import { AttestationType, eventData, nft, NFTCache } from '#types/loreTypes.tsx'
import Button from '#app/components/buttons/button'
import Section from '#app/components/layout/section'
import TypographyParagraph from '#app/components/typography/TypographyParagraph'
import { formatString } from '#app/utils/misc.tsx'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import videoPlaceholder from '#images/video-placeholder.jpg'

export async function loader({ request, params }: LoaderFunctionArgs) {
	const url = new URL(request.url)
	if (params.cid) {
		let nftData
		let metadata: AttestationType | undefined = undefined
		let nftAttestations = [] as AttestationType[] | undefined
		let rollupEventData: eventData[] | undefined = undefined
		try {
			let retrievedMintedMetadata = await getAttestationv2({
				attestationIPFSCID: params.cid,
			})
			const returnedMetadata = retrievedMintedMetadata.attestations[0]
			if (returnedMetadata) {
				metadata = returnedMetadata
			} else {
				console.error('ERROR LOADING DATA FOR ROLLUP', { cid: params.cid })
				throw new Response('Error retrieving attestation for rollup!', {
					status: 500,
				})
			}
		} catch (e) {
			console.error('ERROR LOADING DATA FOR ROLLUP', { e, cid: params.cid })
			throw new Response('Error retrieving attestation for rollup!', {
				status: 500,
			})
		}
		if (metadata && metadata?.properties.rollupCID) {
			try {
				const returnedNFTAttestations =
					await getDerivedAttestationsForRollupCID({
						rollupCid: metadata?.properties.rollupCID,
					})
				if (returnedNFTAttestations) {
					nftAttestations = returnedNFTAttestations
				} else {
					console.error('ERROR LOADING DERIVED ATTESTATIONS FOR ROLLUP', {
						cid: params.cid,
					})
					throw new Response(
						'Error retrieving derived attestations for rollup!',
						{
							status: 500,
						},
					)
				}
			} catch (e) {
				console.error('ERROR LOADING DERIVED ATTESTATIONS FOR ROLLUP', {
					e,
					cid: params.cid,
				})
				throw new Response(
					'Error retrieving derived attestations for rollup!',
					{
						status: 500,
					},
				)
			}
			if (nftAttestations) {
				try {
					const nftsDataRollup = await getEventDataByRollupCID(
						metadata?.properties.rollupCID,
					)
					if (nftsDataRollup) {
						rollupEventData = nftsDataRollup
					} else {
						console.error('NO RETURNED Event Data BY Rollup CID', {
							rollupCID: metadata.properties.rollupCID,
						})
					}
				} catch (e) {
					console.error('NO RETURNED Event Data BY Rollup CID', {
						e,
						rollupCID: metadata.properties.rollupCID,
					})
				}
				if (rollupEventData && rollupEventData.length) {
					nftData = (
						await Promise.all(
							rollupEventData.map(async (nftEntry: eventData) => {
								try {
									const entryNFTData = JSON.parse(nftEntry.metadata) as NFTCache
									// @ts-ignore
									let nftData: nft = { ...entryNFTData }
									if (!nftData || !nftData.contract || !nftData.tokenId) {
										return
									}
									const relatedAttestation = nftAttestations?.find(
										attestation =>
											attestation.references.entry?.nft.contractAddress &&
											attestation.references.entry?.nft.tokenId &&
											nftData.contract.address
												? attestation.references.entry?.nft.contractAddress.toLowerCase() ==
														nftData.contract.address.toLowerCase() &&
													attestation.references.entry?.nft.tokenId.toLowerCase() ==
														nftData.tokenId.toLowerCase()
												: attestation.references.entry?.nft.tokenId &&
													attestation.references.entry?.nft.tokenId.toLowerCase() ==
														nftData.tokenId.toLowerCase(),
									)
									if (!relatedAttestation) {
										return
									}
									nftData.chain =
										relatedAttestation.references &&
										relatedAttestation.references.entry &&
										relatedAttestation.references.entry &&
										relatedAttestation.references.entry.chain &&
										relatedAttestation.references.entry.chain.name
											? relatedAttestation.references.entry.chain.name
											: undefined
									if (!nftData.name) {
										nftData.name = `#${nftData.tokenId}`
									} else {
										nftData.name = formatString(nftData.name)
									}
									if (!nftData.description) {
										nftData.description = 'Description unavailable'
									} else {
										nftData.description = formatString(nftData.description)
									}
									nftData.contentType =
										entryNFTData.image && entryNFTData.image.contentType
											? entryNFTData.image.contentType
											: undefined
									if (nftData.contentType === 'video/mp4') {
										nftData.previewImage = videoPlaceholder
									} else if (
										entryNFTData.image &&
										entryNFTData.image.thumbnailUrl
									) {
										nftData.previewImage = entryNFTData.image.thumbnailUrl
									} else if (
										entryNFTData.image &&
										entryNFTData.image.originalUrl
									) {
										nftData.previewImage = entryNFTData.image.originalUrl
									} else {
										// Resize image with cloudinary
										nftData.previewImage = undefined
									}
									return nftData
								} catch (e) {
									console.error(e)
								}
							}),
						)
					).filter(elem => elem != undefined)
				}
			}
		}

		return defer({
			event: metadata.event,
			nftData,
			atomicLoreUrl: url.origin,
			cid: params.cid,
			metadataEntry: metadata,
			nftAttestations: nftAttestations,
		})
	}
}

export default function metadataPage() {
	const { event, nftData, cid, metadataEntry, nftAttestations, atomicLoreUrl } =
		useLoaderData<{
			event: eventData
			nftData: nft[]
			cid: string
			metadataEntry: AttestationType
			nftAttestations: AttestationType[]
			atomicLoreUrl: string
		}>()

	return (
		<MetadataProfile
			event={event}
			nftData={nftData}
			cid={cid}
			metadataEntry={metadataEntry}
			nftAttestations={nftAttestations}
			atomicLoreUrl={atomicLoreUrl}
		/>
	)
}

export function ErrorBoundary() {
	const error = useRouteError()
	captureRemixErrorBoundaryError(error)

	if (isRouteErrorResponse(error)) {
		console.error('Error is:', error)
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading this Attestation
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Status: {error.status} - {error.statusText}
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Data: {error.data}
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
			</Section>
		)
	} else if (error instanceof Error) {
		console.error('Error is:', error)
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading this Attestation
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Message: {error.message}
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
			</Section>
		)
	} else {
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An unknown error occurred while loading this Attestation
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
			</Section>
		)
	}
}
